import React, { type ReactElement } from 'react'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material'

import { Button, Icon, Typography } from '~components/internal'
import { lightTokens } from '~tokens/internal'

import './ConfirmDeleteModal.scss'

type ConfirmDeleteModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title?: string
  description?: string
  itemName?: string
  isLoading?: boolean
}

const ConfirmDeleteModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Deletion',
  description = 'Are you sure you want to delete this item? This action cannot be undone.',
  itemName,
  isLoading = false,
}: ConfirmDeleteModalProps): ReactElement => {
  const handleCancelClick = (e: React.MouseEvent): void => {
    e.stopPropagation()
    onClose()
  }

  const handleConfirmClick = (e: React.MouseEvent): void => {
    e.stopPropagation()
    onConfirm()
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby='confirm-delete-dialog-title'
      aria-describedby='confirm-delete-dialog-description'
      maxWidth='xs'
      fullWidth
    >
      <DialogTitle id='confirm-delete-dialog-title'>
        <Stack direction='row' className='internal-confirm-delete-modal__title'>
          <Icon name='warning' color={lightTokens.error.main} />
          <Typography variant='h6'>{title}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id='confirm-delete-dialog-description'>
          {itemName
            ? `Are you sure you want to delete "${itemName}"? This action cannot be undone.`
            : description}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelClick} variant='outlined'>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmClick}
          color='error'
          variant='contained'
          autoFocus
          loading={isLoading}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmDeleteModal
